import { dismissTopError, errorStack } from "../../stores/errors";
import { useFocusTrap } from "./use-focus-trap";

/**
 * Global error dialog that displays errors from the error stack.
 * Shows the topmost error in a modal overlay.
 */
export function ErrorDialog() {
  const errors = errorStack.value;
  const topError = errors.length > 0 ? errors[errors.length - 1] : null;
  const open = topError !== null;

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open,
    onEscape: dismissTopError,
  });

  if (!topError) return null;

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-dialog-title"
    >
      {/* Backdrop */}
      <div
        class="absolute inset-0 bg-black/50 transition-opacity"
        onClick={dismissTopError}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
      >
        <div class="p-6">
          <h2
            id="error-dialog-title"
            class="text-lg font-semibold text-text mb-2"
          >
            {topError.title}
          </h2>
          {topError.message && (
            <p class="text-sm text-text-muted">{topError.message}</p>
          )}
          {errors.length > 1 && (
            <p class="mt-2 text-xs text-text-subtle">
              +{errors.length - 1} more{" "}
              {errors.length === 2 ? "error" : "errors"}
            </p>
          )}
        </div>

        <div class="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={dismissTopError}
            class="flex-1 px-4 py-2 rounded-lg border border-border bg-surface text-text text-sm font-medium hover:bg-surface-hover transition-colors"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            class="flex-1 px-4 py-2 rounded-lg bg-text text-surface text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
}
